import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { FAQ } from "@/components/site/article/FAQ";
import { getVolatilityHeatmap, type HeatmapDTO } from "@/lib/volatility-heatmap.functions";

const SITE = "https://xauproacademy.com";
const PATH = "/xauusd-volatility-heatmap";
const PAGE_URL = `${SITE}${PATH}`;
const TITLE = "XAUUSD Volatility Heatmap by Hour and Day | XAU Pro Academy";
const DESC =
  "Average gold (XAUUSD) hourly range by weekday and UTC hour from the last 90 days of spot data. See the best time to trade gold, the quietest hours and how volatility shifts across sessions.";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

const FAQS = [
  { q: "What is the best time to trade gold?", a: "Gold typically delivers its largest hourly ranges from the London open (07:00 UTC) through the New York overlap (12:00 to 16:00 UTC). The heatmap measures this directly, so you can see which hours and weekdays currently carry the most range." },
  { q: "How is the heatmap calculated?", a: "Each cell is the average high to low range of one H1 bar for that weekday and UTC hour, using XAUUSD spot bars from the last 90 days. Hours with incomplete data are excluded, and a cell needs at least three samples." },
  { q: "Why are some hours much quieter?", a: "Late New York and early Asian hours have thinner liquidity. Ranges compress, spreads can widen and price often builds the range that London later tests. Low range hours suit observation rather than aggressive execution." },
  { q: "Does high volatility mean a better trade?", a: "No. Volatility describes how far price tends to move, not its direction. High range hours offer more distance to targets but also remove poorly placed stops faster. Setup quality still depends on structure, liquidity and displacement." },
  { q: "Why do some days stand out?", a: "Scheduled macro releases cluster on specific weekdays. US CPI and NFP releases often lift Thursday and Friday afternoon cells, and FOMC decisions lift Wednesday evening. The averages include those events." },
  { q: "Which time zone does the heatmap use?", a: "All hours are UTC. Use the local time toggle to read the grid in your browser's time zone." },
];

export const Route = createFileRoute("/xauusd-volatility-heatmap")({
  loader: async () => {
    const data = await getVolatilityHeatmap().catch(() => null);
    return { data };
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "keywords", content: "xauusd volatility by hour, gold volatility heatmap, best time to trade gold, gold average pip movement by day, xauusd hourly range, gold volatility by session" },
      { property: "og:title", content: "XAUUSD Volatility Heatmap by Hour and Day" },
      { property: "og:description", content: "Average gold hourly range by weekday and UTC hour. Find the most active and the quietest hours to trade XAUUSD." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "XAUUSD Volatility Heatmap",
          url: PAGE_URL,
          applicationCategory: "FinanceApplication",
          operatingSystem: "Any",
          description: DESC,
          isAccessibleForFree: true,
          inLanguage: "en",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "XAUUSD Volatility Heatmap", item: PAGE_URL },
          ],
        }),
      },
    ],
  }),
  errorComponent: () => <Layout><p className="mx-auto max-w-5xl px-4 py-12 text-muted-foreground">Volatility data is temporarily unavailable.</p></Layout>,
  notFoundComponent: () => <Layout><p className="mx-auto max-w-5xl px-4 py-12 text-muted-foreground">Not found.</p></Layout>,
  component: HeatmapPage,
});

import { VolatilityHeatmapPanel, usd, hh, summarize } from "@/components/tools/VolatilityHeatmapPanel";

function HeatmapPage() {
  const { data } = Route.useLoaderData();
  const s = useMemo(() => (data ? summarize(data) : null), [data]);

  if (!data || !s) {
    return (
      <Layout>
        <article className="mx-auto max-w-5xl px-4 py-12">
          <h1 className="text-3xl font-semibold">XAUUSD Volatility Heatmap</h1>
          <p className="mt-4 text-muted-foreground">Volatility data is temporarily unavailable. Please check back shortly.</p>
        </article>
      </Layout>
    );
  }

  const best = s.top[0];
  return (
    <Layout>
      <article className="mx-auto max-w-5xl px-4 py-12">
        <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Market Tools / XAUUSD</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight md:text-5xl">XAUUSD Volatility Heatmap</h1>
        <p className="mt-5 max-w-3xl text-lg leading-relaxed text-muted-foreground">
          Average gold range for every weekday and hour over the last {data.days} trading days. The most active cell is{" "}
          <strong className="text-foreground">{best ? `${DAYS[best.w]} ${hh(best.h)} UTC (${usd(best.v)})` : "n/a"}</strong>. Use it to see when gold usually moves and when it usually waits.
        </p>

        <VolatilityHeatmapPanel data={data} />

        <section className="pt-14">
          <h2 className="text-2xl font-semibold">How to read it</h2>
          <div className="mt-4 space-y-4 leading-relaxed text-muted-foreground">
            <p>Volatility describes how far price usually travels, not where it goes. High range hours give more distance to targets but punish poorly placed stops. Low range hours often build the liquidity that the next session raids.</p>
            <p>Use the grid to plan when to be attentive, then let structure, liquidity and displacement decide whether a setup exists. Size positions to the hour's range instead of tightening stops.</p>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button asChild variant="gold"><Link to="/gold-daily-range-atr">Gold daily range and ATR <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
            <Button asChild variant="outline"><Link to="/gold-trading-sessions">Live session clock</Link></Button>
          </div>
        </section>

        <section className="pt-14"><FAQ items={FAQS} /></section>

        <aside className="mt-10 rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold">Related references</h2>
          <ul className="mt-3 grid gap-2 text-sm md:grid-cols-2">
            <li><Link to="/position-size-calculator" className="text-gold hover:underline">Position size calculator</Link></li>
            <li><Link to="/gold-scalping-strategy" className="text-gold hover:underline">Gold scalping strategy</Link></li>
            <li><Link to="/xauusd-analysis-today" className="text-gold hover:underline">XAUUSD analysis today</Link></li>
            <li><Link to="/gold-trading-strategy" className="text-gold hover:underline">Gold trading strategy playbook</Link></li>
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">Educational tool. Readings derive from XAUUSD spot bars, refresh hourly, and may differ slightly from your broker's feed. Past volatility does not guarantee future ranges. Trading leveraged products carries substantial risk.</p>
        </aside>
      </article>
    </Layout>
  );
}
